import { useState, useEffect, useCallback } from 'react';
import { getAccessTokenSync } from './useAccessToken';

const SB_URL = process.env.REACT_APP_SUPABASE_URL;
const SB_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

function headers() {
  const token = getAccessTokenSync();
  return {
    'apikey': SB_KEY,
    'Authorization': `Bearer ${token || SB_KEY}`,
    'Content-Type': 'application/json',
  };
}

// Map DB snake_case row to frontend camelCase
function toFrontend(row) {
  return {
    id: row.id,
    name: row.name,
    section: row.section,
    assignedTo: row.assigned_to,
    reviewer: row.reviewer,
    dueDate: row.due_date,
    done: row.done || false,
    completedBy: row.completed_by || null,
    completedAt: row.completed_at || null,
    reviewed: row.reviewed || false,
    reviewedBy: row.reviewed_by || null,
    reviewedAt: row.reviewed_at || null,
    blocked: row.blocked || false,
    blockedReason: row.blocked_reason || null,
    documents: row.documents || [],
    workbook: row.workbook || null,
    accounts: row.accounts || null,
  };
}

/**
 * Fetches close tasks from Supabase for a given company + month,
 * and persists toggleComplete / toggleReview changes.
 */
export default function useCloseTasks(companyId, month, userInitials) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch tasks for this company + month
  const fetchTasks = useCallback(async () => {
    if (!SB_URL || !SB_KEY || !companyId || !month) {
      setTasks([]);
      setLoading(false);
      return;
    }
    setTasks([]);
    setLoading(true);
    try {
      const res = await fetch(
        `${SB_URL}/rest/v1/close_tasks?company_id=eq.${companyId}&month=eq.${month}&order=id.asc`,
        { headers: headers() }
      );
      if (!res.ok) throw new Error(`${res.status}`);
      const rows = await res.json();
      setTasks(rows.map(toFrontend));
    } catch (err) {
      console.error('useCloseTasks fetch error:', err);
      setTasks([]);
    }
    setLoading(false);
  }, [companyId, month]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Persist a partial update to a task row
  const patchTask = useCallback(async (taskId, updates) => {
    try {
      await fetch(`${SB_URL}/rest/v1/close_tasks?id=eq.${taskId}`, {
        method: 'PATCH',
        headers: { ...headers(), 'Prefer': 'return=minimal' },
        body: JSON.stringify(updates),
      });
    } catch (err) {
      console.error('useCloseTasks patch error:', err);
    }
  }, []);

  const toggleComplete = useCallback((taskId) => {
    setTasks(prev => prev.map(task => {
      if (task.id !== taskId) return task;

      // Can't undo if already reviewed
      if (task.done && task.reviewed) return task;
      // Can't undo someone else's work
      if (task.done && task.completedBy !== userInitials) return task;

      const nowDone = !task.done;
      const updates = {
        done: nowDone,
        completed_by: nowDone ? userInitials : null,
        completed_at: nowDone ? new Date().toISOString() : null,
        reviewed: false,
        reviewed_by: null,
        reviewed_at: null,
      };
      patchTask(taskId, updates);
      return {
        ...task,
        done: nowDone,
        completedBy: updates.completed_by,
        completedAt: updates.completed_at,
        reviewed: false,
        reviewedBy: null,
        reviewedAt: null,
      };
    }));
  }, [userInitials, patchTask]);

  const toggleReview = useCallback((taskId) => {
    setTasks(prev => prev.map(task => {
      if (task.id !== taskId || !task.done) return task;
      // Can't undo someone else's review
      if (task.reviewed && task.reviewedBy !== userInitials) return task;
      // Can't review your own work
      if (!task.reviewed && task.completedBy === userInitials) return task;

      const nowReviewed = !task.reviewed;
      const updates = {
        reviewed: nowReviewed,
        reviewed_by: nowReviewed ? userInitials : null,
        reviewed_at: nowReviewed ? new Date().toISOString() : null,
      };
      patchTask(taskId, updates);
      return {
        ...task,
        reviewed: nowReviewed,
        reviewedBy: updates.reviewed_by,
        reviewedAt: updates.reviewed_at,
      };
    }));
  }, [userInitials, patchTask]);

  const toggleBlocked = useCallback((taskId, blocked, reason) => {
    const updates = {
      blocked: blocked,
      blocked_reason: blocked ? (reason || 'Blocked') : null,
    };
    patchTask(taskId, updates);
    setTasks(prev => prev.map(task => {
      if (task.id !== taskId) return task;
      return { ...task, blocked, blockedReason: updates.blocked_reason };
    }));
  }, [patchTask]);

  const updateDeadline = useCallback((taskId, newDate) => {
    setTasks(prev => prev.map(task =>
      task.id === taskId ? { ...task, dueDate: newDate } : task
    ));
    patchTask(taskId, { due_date: newDate });
  }, [patchTask]);

  const addTask = useCallback((taskData) => {
    const newId = Math.max(0, ...tasks.map(t => t.id), 0) + 1;
    const newTask = {
      id: newId,
      name: taskData.name || 'New task',
      section: taskData.section || '',
      panelId: taskData.panelId || null,
      assignedTo: taskData.assignedTo || userInitials || '',
      reviewer: taskData.reviewer || '',
      dueDate: taskData.dueDate || null,
      done: false,
      completedBy: null,
      completedAt: null,
      reviewed: false,
      reviewedBy: null,
      reviewedAt: null,
      blocked: false,
      blockedReason: null,
      documents: [],
      workbook: null,
      accounts: taskData.accounts || null,
      description: taskData.description || '',
    };
    setTasks(prev => [...prev, newTask]);

    // Persist to Supabase if connected
    if (SB_URL && SB_KEY && companyId && month) {
      const row = {
        company_id: companyId,
        month: month,
        name: newTask.name,
        section: newTask.section,
        assigned_to: newTask.assignedTo,
        reviewer: newTask.reviewer,
        due_date: newTask.dueDate,
        done: false,
        blocked: false,
        documents: [],
        accounts: newTask.accounts,
      };
      fetch(`${SB_URL}/rest/v1/close_tasks`, {
        method: 'POST',
        headers: { ...headers(), 'Prefer': 'return=representation' },
        body: JSON.stringify(row),
      }).then(res => res.ok ? res.json() : null).then(rows => {
        if (rows && rows[0]) {
          // Update the local task with the real DB id
          setTasks(prev => prev.map(t => t.id === newId ? { ...t, id: rows[0].id } : t));
        }
      }).catch(err => console.error('addTask error:', err));
    }

    return newTask;
  }, [tasks, userInitials, companyId, month]);

  const removeTask = useCallback((taskId) => {
    setTasks(prev => prev.filter(t => t.id !== taskId));
    // Persist to Supabase
    if (SB_URL && SB_KEY) {
      fetch(`${SB_URL}/rest/v1/close_tasks?id=eq.${taskId}`, {
        method: 'DELETE',
        headers: headers(),
      }).catch(err => console.error('removeTask error:', err));
    }
  }, []);

  return { tasks, loading, toggleComplete, toggleReview, toggleBlocked, updateDeadline, addTask, removeTask, refetch: fetchTasks };
}
